import React, { useState, useMemo } from 'react';
import {
    Box, Grid, Typography, Divider, Paper, Stack,
    TextField, InputAdornment, Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import GroupAddRoundedIcon from '@mui/icons-material/GroupAddRounded';
import Back from '../../../common/components/BackNavigateComponent';
import { useGroupList } from '../../../common/API/hooks';
import GroupTable from './GroupTable';
import GroupDrawer from './GroupDrawer';
import Fuse from 'fuse.js';
import { useDebounce } from 'use-debounce';

const BRAND = '#0F2FEB';

function StatCard({ icon, label, value, color, bgColor, loading }) {
    return (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderLeft: `4px solid ${color}` }}>
            <Stack direction="row" alignItems="center" spacing={2}>
                <Box sx={{ p: 1, borderRadius: 2, bgcolor: bgColor, color, display: 'flex' }}>
                    {React.cloneElement(icon, { fontSize: 'medium' })}
                </Box>
                <Box>
                    <Typography variant="h5" fontWeight="bold" color={color}>
                        {loading ? '—' : value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">{label}</Typography>
                </Box>
            </Stack>
        </Paper>
    );
}

export default function GroupManagement() {
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch] = useDebounce(searchQuery, 300);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerMode, setDrawerMode] = useState('add');
    const [selectedGroup, setSelectedGroup] = useState(null);

    const { data: groupList, isLoading } = useGroupList();

    const filteredGroups = useMemo(() => {
        if (!groupList) return [];
        if (!debouncedSearch) return groupList;
        const fuse = new Fuse(groupList, { keys: ['name'], threshold: 0.3 });
        return fuse.search(debouncedSearch).map(r => r.item);
    }, [groupList, debouncedSearch]);

    const stats = useMemo(() => {
        if (!groupList) return { total: 0, totalPerms: 0, avgUsers: 0 };
        const totalPerms = groupList.reduce((sum, g) => sum + (g.permissions?.length || 0), 0);
        const totalUsers = groupList.reduce((sum, g) => sum + (g.users?.length || 0), 0);
        return {
            total: groupList.length,
            totalPerms,
            avgUsers: groupList.length ? Math.round(totalUsers / groupList.length) : 0,
        };
    }, [groupList]);

    const handleAddGroup = () => {
        setSelectedGroup(null);
        setDrawerMode('add');
        setDrawerOpen(true);
    };

    const handleEditGroup = (group) => {
        setSelectedGroup(group);
        setDrawerMode('edit');
        setDrawerOpen(true);
    };

    return (
        <Box sx={{ pb: 4 }}>
            {/* Header */}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 3, mb: 2 }}>
                <Back />
                <Typography variant="h5" fontWeight="bold" sx={{ ml: 1 }}>
                    Group Management
                </Typography>
            </Stack>
            <Divider sx={{ borderBottom: 3, mb: 3 }} />

            {/* Stats */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                    <StatCard icon={<GroupsRoundedIcon />} label="Total Groups" value={stats.total}
                        color={BRAND} bgColor="#EEF1FD" loading={isLoading} />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <StatCard icon={<LockRoundedIcon />} label="Total Permissions" value={stats.totalPerms}
                        color="#6A1B9A" bgColor="#F3E5F5" loading={isLoading} />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <StatCard icon={<PeopleAltRoundedIcon />} label="Avg. Members / Group" value={stats.avgUsers}
                        color="#00695C" bgColor="#E0F2F1" loading={isLoading} />
                </Grid>
            </Grid>

            {/* Toolbar */}
            <Paper variant="outlined" sx={{
                borderRadius: 2, borderBottomLeftRadius: 0, borderBottomRightRadius: 0,
                p: 1.5, bgcolor: '#F8F9FA',
            }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}
                    alignItems={{ sm: 'center' }} justifyContent="space-between">
                    <TextField
                        size="small"
                        placeholder="Search groups by name…"
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        sx={{ bgcolor: 'white', minWidth: { sm: 300 }, width: { xs: '100%', sm: 'auto' } }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        variant="contained"
                        sx={{ bgcolor: BRAND, textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
                        startIcon={<GroupAddRoundedIcon />}
                        onClick={handleAddGroup}
                    >
                        Add Group
                    </Button>
                </Stack>
            </Paper>

            {/* Results count */}
            {!isLoading && groupList && (
                <Box sx={{ px: 2, py: 0.75, bgcolor: '#FAFAFA', borderLeft: '2px solid #E0E0E0', borderRight: '2px solid #E0E0E0' }}>
                    <Typography variant="caption" color="text.secondary">
                        {debouncedSearch
                            ? `Showing ${filteredGroups.length} of ${groupList.length} groups`
                            : `${groupList.length} group${groupList.length !== 1 ? 's' : ''} total`}
                    </Typography>
                </Box>
            )}

            {/* Table */}
            <Paper variant="outlined" sx={{
                borderRadius: 2, borderTopLeftRadius: 0, borderTopRightRadius: 0, borderTop: 0,
            }}>
                <GroupTable filteredGroups={filteredGroups} isLoading={isLoading} onEditGroup={handleEditGroup} />
            </Paper>

            {/* Drawer */}
            <GroupDrawer
                isOpen={drawerOpen}
                mode={drawerMode}
                group={selectedGroup}
                onClose={() => setDrawerOpen(false)}
            />
        </Box>
    );
}